import type { ISupportIssueAttachment } from '@commenda/api';
import type { ILineItemInput } from '@commenda/api/new-sales-tax/calculate/calculate';
import type { UIMessage } from 'ai';

import { computeTaxSummary } from '../indirect-tax/calculator/computeTaxSummary';
import type {
  RawCalculateResponse,
  RawTaxLineItem,
  TaxSummaryView,
} from '../indirect-tax/calculator/types';

type MessagePart = UIMessage['parts'][number];

/** A tool result the chat renders as a card instead of folding into the chain of thought. */
export type ChatCard =
  | {
      kind: 'accountManager';
      managers: {
        name: string | null;
        email: string;
        bookingLink: string | null;
      }[];
    }
  | {
      kind: 'issueDraft';
      subject: string;
      message: string;
      companyId: number | null;
      destination: string | null;
      files: ISupportIssueAttachment[];
    }
  | {
      kind: 'taxCalculation';
      entityName: string | null;
      addressLabel: string;
      isStreetGiven: boolean;
      isNoStateSalesTax: boolean;
      summary: TaxSummaryView;
      calculatorLink: string | null;
    }
  | { kind: 'documentReading'; runId: string };

/** Maps a finished tool part to the card it should render, or null when it has none. Only
 * `output-available` parts qualify: a failed or still-running call keeps its normal tool row.
 *
 * Tool payloads are untyped at this boundary — the AI SDK types a tool part's `output` as `unknown`
 * and MCP hands the payload back as JSON text — so every field is narrowed before use and any shape
 * that does not match returns null, leaving the part to render as an ordinary tool row. */
export class ChatCardHelper {
  static forPart(part: MessagePart): ChatCard | null {
    if (ChatCardHelper.stateOf(part) !== 'output-available') return null;

    const toolName = ChatCardHelper.toolNameOf(part);
    if (!toolName) return null;

    const payload = ChatCardHelper.unwrapToolPayload(
      ChatCardHelper.outputOf(part)
    );
    if (!payload) return null;

    if (toolName === 'list_account_managers')
      return ChatCardHelper.accountManagerCard(payload);
    if (toolName === 'create_support_issue')
      return ChatCardHelper.issueDraftCard(payload);
    if (toolName === 'calculate_tax')
      return ChatCardHelper.taxCalculationCard(payload);
    if (toolName === 'start_document_reading')
      return ChatCardHelper.documentReadingCard(payload);
    return null;
  }

  /** A finished tool call's name, input and unwrapped payload, for callers that read a tool's result
   * without rendering a card; null unless the part is `output-available` with a payload. */
  static toolResultOf(part: MessagePart): {
    toolName: string;
    input: Record<string, unknown> | null;
    payload: Record<string, unknown>;
  } | null {
    if (ChatCardHelper.stateOf(part) !== 'output-available') return null;

    const toolName = ChatCardHelper.toolNameOf(part);
    if (!toolName) return null;

    const payload = ChatCardHelper.unwrapToolPayload(
      ChatCardHelper.outputOf(part)
    );
    if (!payload) return null;

    const input = ChatCardHelper.asRecord(
      ChatCardHelper.parseMaybeJson(ChatCardHelper.inputOf(part))
    );
    return { toolName, input, payload };
  }

  /** The run a data room reading started, whose card then follows it live. A refused start carries no
   * run id and keeps its normal tool row. */
  private static documentReadingCard(
    payload: Record<string, unknown>
  ): ChatCard | null {
    const runId = ChatCardHelper.asText(payload.runId);
    return runId ? { kind: 'documentReading', runId } : null;
  }

  /** The address the tax engine priced, as it reports it back, with the calculator's own summary of the
   * result. Address parts the agent left out come back as empty strings, not missing keys. An address that
   * matched no taxing jurisdiction has no card, so it never reads as 0%, unless the tool reports the ZIP is
   * inside a state with no state sales tax. */
  private static taxCalculationCard(
    payload: Record<string, unknown>
  ): ChatCard | null {
    const result = ChatCardHelper.asRecord(payload.result);
    if (!result || !ChatCardHelper.isTaxCalculation(result)) return null;

    const summary = computeTaxSummary(
      result,
      ChatCardHelper.requestLineItems(payload.lineItems)
    );
    if (!summary) return null;

    const { ship_to: shipTo } = result.data.addresses;
    const street = ChatCardHelper.asText(shipTo.address_line_1);
    const hasJurisdiction = result.data.line_items.some(
      (lineItem) => lineItem.tax_breakdown.length > 0
    );
    const isNoStateSalesTax =
      !hasJurisdiction && payload.isInNoStateSalesTaxState === true;
    if (!hasJurisdiction && !isNoStateSalesTax) return null;

    return {
      kind: 'taxCalculation',
      entityName: ChatCardHelper.asText(payload.entityName),
      addressLabel: [
        street,
        ChatCardHelper.asText(shipTo.city),
        `${shipTo.state} ${shipTo.postal_code}`,
        shipTo.country,
      ]
        .filter(Boolean)
        .join(', '),
      isStreetGiven: street !== null,
      isNoStateSalesTax,
      summary,
      calculatorLink: ChatCardHelper.asText(payload.link),
    };
  }

  /** Checks every field the card and {@link computeTaxSummary} read, so a partial result falls back to the
   * ordinary tool row instead of rendering NaN or a blank address. */
  private static isTaxCalculation(
    result: Record<string, unknown>
  ): result is Record<string, unknown> & RawCalculateResponse {
    const calculation = ChatCardHelper.asRecord(result.data);
    const shipTo = ChatCardHelper.asRecord(
      ChatCardHelper.asRecord(calculation?.addresses)?.ship_to
    );
    const isAddressComplete = ['state', 'postal_code', 'country'].every(
      (key) => ChatCardHelper.asText(shipTo?.[key]) !== null
    );
    const lineItems = calculation?.line_items;

    return (
      isAddressComplete &&
      Array.isArray(lineItems) &&
      lineItems.length > 0 &&
      lineItems.every(ChatCardHelper.isPricedLineItem)
    );
  }

  private static isPricedLineItem(value: unknown): value is RawTaxLineItem {
    const lineItem = ChatCardHelper.asRecord(value);
    const breakdown = lineItem?.tax_breakdown;

    return (
      typeof lineItem?.taxable_amount === 'number' &&
      typeof lineItem.effective_tax_rate === 'number' &&
      Array.isArray(breakdown) &&
      breakdown.every((row) => {
        const jurisdiction = ChatCardHelper.asRecord(row);
        return (
          typeof jurisdiction?.jurisdiction_name === 'string' &&
          typeof jurisdiction.jurisdiction_type === 'string' &&
          typeof jurisdiction.rate === 'number'
        );
      })
    );
  }

  /** The line items the tool priced. The engine echoes each line's amount as price × quantity, so the
   * invoice total has to come from the request rather than the response. */
  private static requestLineItems(
    value: unknown
  ): ILineItemInput[] | undefined {
    if (!Array.isArray(value)) return undefined;

    const lineItems = value.flatMap((entry) => {
      const lineItem = ChatCardHelper.asRecord(entry);
      if (
        typeof lineItem?.lineNumber !== 'number' ||
        typeof lineItem.pricePerUnit !== 'number' ||
        typeof lineItem.quantity !== 'number'
      )
        return [];
      return [
        {
          lineNumber: lineItem.lineNumber,
          pricePerUnit: lineItem.pricePerUnit,
          quantity: lineItem.quantity,
          discount:
            typeof lineItem.discount === 'number'
              ? lineItem.discount
              : undefined,
        },
      ];
    });

    return lineItems.length > 0 ? lineItems : undefined;
  }

  private static accountManagerCard(
    payload: Record<string, unknown>
  ): ChatCard | null {
    const rows = payload.accountManagers;
    if (!Array.isArray(rows)) return null;

    const managers = rows.flatMap((row) => {
      const record = ChatCardHelper.asRecord(row);
      const email = ChatCardHelper.asText(record?.email);
      // Without an email there is no contact method, so the card would have nothing to offer.
      if (!email) return [];
      return [
        {
          name: ChatCardHelper.asText(record?.name),
          email,
          bookingLink: ChatCardHelper.asText(record?.bookingLink),
        },
      ];
    });

    return managers.length > 0 ? { kind: 'accountManager', managers } : null;
  }

  private static issueDraftCard(
    payload: Record<string, unknown>
  ): ChatCard | null {
    if (payload.requiresConfirmation !== true) return null;

    const draft = ChatCardHelper.asRecord(payload.draft);
    const subject = ChatCardHelper.asText(draft?.subject);
    const message = ChatCardHelper.asText(draft?.message);
    if (!subject || !message) return null;

    return {
      kind: 'issueDraft',
      subject,
      message,
      companyId: typeof draft?.companyId === 'number' ? draft.companyId : null,
      destination: ChatCardHelper.asText(draft?.destination),
      files: ChatCardHelper.draftFiles(draft?.files),
    };
  }

  /** The chat's files the agent put on the draft, one per url: the same file attached twice in a
   * conversation is still one attachment on the ticket. */
  private static draftFiles(value: unknown): ISupportIssueAttachment[] {
    if (!Array.isArray(value)) return [];

    const files = value.flatMap((entry) => {
      const record = ChatCardHelper.asRecord(entry);
      const fileUrl = ChatCardHelper.asText(record?.fileUrl);
      const fileName = ChatCardHelper.asText(record?.fileName);
      return fileUrl && fileName ? [{ fileUrl, fileName }] : [];
    });

    return Array.from(
      new Map(files.map((file) => [file.fileUrl, file])).values()
    );
  }

  /** MCP results wrap their payload in `content: [{ type: 'text', text: '<json>' }]`; some
   * transports hand back the payload directly. Unwrap either shape before reading fields. */
  private static unwrapToolPayload(
    output: unknown
  ): Record<string, unknown> | null {
    const parsed = ChatCardHelper.asRecord(
      ChatCardHelper.parseMaybeJson(output)
    );
    if (!parsed) return null;

    const { content } = parsed;
    if (Array.isArray(content)) {
      const firstText = content.find(
        (entry): entry is { type: string; text: string } =>
          ChatCardHelper.asRecord(entry)?.type === 'text' &&
          typeof ChatCardHelper.asRecord(entry)?.text === 'string'
      );
      if (firstText)
        return ChatCardHelper.asRecord(
          ChatCardHelper.parseMaybeJson(firstText.text)
        );
    }

    return parsed;
  }

  /** Tool inputs/outputs often arrive as a JSON string — parse so fields are readable. */
  private static parseMaybeJson(value: unknown): unknown {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  /** Safe: the typeof/null/array guards establish the shape the assertion claims. */
  private static asRecord(value: unknown): Record<string, unknown> | null {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  }

  private static asText(value: unknown): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value : null;
  }

  /** Safe: `state` is present on every tool part variant, but reading it off the union needs the
   * widening — the non-tool variants in `UIMessage['parts']` do not declare it. */
  private static stateOf(part: MessagePart): string | undefined {
    return (part as { state?: string }).state;
  }

  /** Safe: same widening as {@link stateOf}, for the output payload. */
  private static outputOf(part: MessagePart): unknown {
    return (part as { output?: unknown }).output;
  }

  /** Safe: same widening as {@link stateOf}, for the tool's input. */
  private static inputOf(part: MessagePart): unknown {
    return (part as { input?: unknown }).input;
  }

  /** A server tool arrives as `tool-<name>`; a client-registered one as `dynamic-tool` carrying the
   * name in a field the union does not declare, hence the widening. */
  private static toolNameOf(part: MessagePart): string | null {
    if (part.type === 'dynamic-tool')
      return (part as { toolName?: string }).toolName ?? null;
    return part.type.startsWith('tool-')
      ? part.type.slice('tool-'.length)
      : null;
  }
}
